/**
 * WebSocket Service for Real-Time Collaboration
 * Manages WebSocket connections and real-time data synchronization
 */

import { io, Socket } from 'socket.io-client';

/**
 * WS_TRACE diagnostics (gated by localStorage.WS_TRACE === '1'). Logs one line
 * per received event / join / identify, with epoch=Date.now() so the browser
 * timeline aligns with the backend's [WSTRACE] lines despite UTC↔local skew.
 * Enable in the browser console: localStorage.WS_TRACE='1' (then reload).
 * Temporary instrumentation — dormant unless the flag is set.
 */
function wsTraceEnabled(): boolean {
  try { return localStorage.getItem('WS_TRACE') === '1'; } catch { return false; }
}
function wsTrace(kind: string, event: string, extra?: Record<string, any>): void {
  if (!wsTraceEnabled()) return;
  const ids = extra
    ? Object.entries(extra).filter(([, v]) => v != null && v !== '').map(([k, v]) => `${k}=${v}`).join(' ')
    : '';
  // eslint-disable-next-line no-console
  console.log(`[WSTRACE] ${new Date().toISOString()} epoch=${Date.now()} side=FE kind=${kind} event=${event}${ids ? ' ' + ids : ''}`);
}

export interface WebSocketMessage {
  type: string;
  payload: any;
  timestamp: number;
  userId?: string;
  sessionId?: string;
}

export interface UserPresence {
  userId: string;
  userName: string;
  userRole: string;
  currentPage: string;
  lastSeen: number;
  isActive: boolean;
}

export interface CollaborationEvent {
  type: 'user_joined' | 'user_left' | 'user_typing' | 'workflow_updated' | 'task_assigned';
  userId: string;
  data: any;
  timestamp: number;
}

export class WebSocketService {
  private socket: Socket | null = null;
  // DEFECT-WS-LATENCY-001: never permanently give up reconnecting. The previous
  // hard cap of 5 attempts (~31s of backoff) left the client silently offline
  // after any transient drop (devtunnel blip / proxy / laptop sleep), so live
  // updates stopped arriving until a manual page refresh. We now retry forever
  // with exponential backoff capped at maxReconnectDelay.
  private reconnectAttempts = 0;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 30000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private isConnecting = false;
  private unauthorizedRefreshAttempted = false;
  private messageHandlers: Map<string, ((data: any) => void)[]> = new Map();
  private presenceHandlers: ((presence: UserPresence[]) => void)[] = [];
  private collaborationHandlers: ((event: CollaborationEvent) => void)[] = [];
  private desiredProjectIds: Set<string> = new Set();

  constructor() {
    this.connect();
  }

  private connect(): void {
    if (this.isConnecting || (this.socket && this.socket.connected)) {
      return;
    }

    this.isConnecting = true;
    
    try {
      // Connect to Socket.IO server
      const serverUrl = import.meta.env.VITE_WS_URL || window.location.origin;
      this.socket = io(serverUrl, {
        auth: (cb: (payload: { token?: string }) => void) => {
          const token = (window as any).__authStore?.getState?.()?.token;
          cb(token ? { token } : {});
        },
        // Polling-first (Socket.IO default order) for reverse-proxy resilience:
        // connect over long-polling immediately (works through Cloudflare/ALB/nginx
        // even when the WebSocket upgrade path is blocked), then transparently
        // upgrade to WebSocket where the proxy supports it. WebSocket-first made the
        // client hang and never fall back when the upgrade was mishandled upstream.
        transports: ['polling', 'websocket'],
        timeout: 20000,
        forceNew: true
      });

      // WS_TRACE checkpoint 4: log EVERY received event (the proof the frame
      // actually reached the browser). onAny fires for all server emits.
      this.socket.onAny((event: string, payload: any) => {
        wsTrace('rx', event, {
          project: payload?.projectId ?? payload?.project_id,
          interventionId: payload?.interventionId,
          executionId: payload?.executionId ?? payload?.execution_id,
          status: payload?.status ?? payload?.statusHint,
          stage: payload?.stage,
        });
      });

      this.socket.on('connect', () => {
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.unauthorizedRefreshAttempted = false;
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
        wsTrace('connect', 'connect', { socketId: this.socket?.id });
        this.identifyUser();
      });
      
      this.socket.on('connected', () => {});
      this.socket.on('user_identified', (data: any) => {
        wsTrace('identified', 'user_identified', { userId: data?.userId, isGuest: data?.isGuest });
        this.rejoinDesiredProjects();
      });
      this.socket.on('project_joined', (data: any) => {
        wsTrace('join_ok', 'project_joined', { project: data?.projectId, users: data?.roomUsers?.length });
        // Self-heal: notify subscribers on every (re)join so they can reconcile any
        // room-scoped events that were missed while the socket was out of the room
        // (e.g. during an auth-token refresh reconnect or page navigation, when emits
        // land on roomSize=0). Handlers typically invalidate their react-query caches.
        const handlers = this.messageHandlers.get('project_joined');
        if (handlers && handlers.length > 0) {
          handlers.forEach(handler => {
            try { requestAnimationFrame(() => handler(data)); } catch (_) { /* ignore */ }
          });
        }
      });
      // BE emits 'error' when a room join is rejected (e.g. guest/unidentified).
      // The FE previously ignored it, so failed joins were invisible.
      this.socket.on('error', (data: any) => {
        wsTrace('srv_error', 'error', { message: data?.message, error: data?.error, code: data?.code });
      });

      this.socket.on('message', (message: WebSocketMessage) => {
        this.handleMessage(message);
      });
      
      // ✅ FIX DEFECT-025: Add direct Socket.IO listeners for backend events
      // Backend emits events directly (not wrapped in 'message' event)
      this.setupDirectEventListeners();

      this.socket.on('disconnect', (reason: any) => {
        wsTrace('disconnect', 'disconnect', { reason });
        this.isConnecting = false;
        this.handleReconnect();
      });

      this.socket.on('connect_error', (error) => {
        console.error('[WebSocket] Connection error:', error);
        this.isConnecting = false;
        const message = String((error as { message?: string })?.message || '');
        if (message === 'unauthorized' && !this.unauthorizedRefreshAttempted) {
          this.unauthorizedRefreshAttempted = true;
          const refresh = (window as any).__authStore?.getState?.()?.refreshAuth;
          if (typeof refresh === 'function') {
            void refresh();
            return;
          }
        }
        this.handleReconnect();
      });

    } catch (error) {
      console.error('[WebSocket] Failed to connect:', error);
      this.isConnecting = false;
      this.handleReconnect();
    }
  }
  
  /**
   * ✅ FIX DEFECT-025: Setup direct Socket.IO event listeners
   * Backend emits events directly (e.g., socket.emit('stage_item_created', data))
   * Not wrapped in 'message' event, so we need direct listeners
   */
  private setupDirectEventListeners(): void {
    if (!this.socket) return;
    
    // List of all events that backend emits directly
    const directEvents = [
      'workflow_started',
      'workflow_status_changed',
      'workflow_completed',
      'stage_started',
      'stage_completed',
      'stage_assigned_to_you',
      'intervention_required',
      'intervention_assigned',
      'intervention_escalated',
      'intervention_resolved',
      'workflow_error',
      'task_assigned',
      'task_assigned_to_you',
      'integration_failed',
      'system_alert',
      'stage_item_created',
      'stage_item_updated',
      'stage_item_deleted',
      'workflow_progress',
      'external_agent_progress',
      'jira_sync_status',
      'monday_sync_status',
      'repository_sync_status',
      'task_queue_updated',
      // DEFECT-WS-LATENCY-002: BE emits ci_status_updated (ci-status-service.ts) but it
      // was never registered here, so CI status changes were silently dropped client-side.
      'ci_status_updated',
      // Cluster B CD: deploy run state changes (deploy-gate-service / deploy-run-poller).
      'deployment_run_updated',
      'system_alerts_cleared',
    ];
    
    directEvents.forEach(eventType => {
      this.socket!.on(eventType, (eventData: any) => {
        const handlers = this.messageHandlers.get(eventType);
        // WS_TRACE checkpoint 5: how many business handlers (query invalidators)
        // run for this event. handlers=0 → received but nothing updates the UI.
        wsTrace('handler', eventType, {
          handlers: handlers?.length ?? 0,
          project: eventData?.projectId ?? eventData?.project_id,
        });
        if (handlers && handlers.length > 0) {
          handlers.forEach(handler => {
            try {
              requestAnimationFrame(() => handler(eventData));
            } catch (error) {
              console.error(`[WebSocket] Handler error for ${eventType}:`, error);
            }
          });
        }
      });
    });
  }

  private handleReconnect(): void {
    // Already have a pending reconnect attempt or a live socket — don't stack timers.
    if (this.reconnectTimer || this.isConnecting || this.socket?.connected) {
      return;
    }

    this.reconnectAttempts++;
    // Exponential backoff capped at maxReconnectDelay so we keep retrying forever
    // without hammering the server. Exponent is clamped to avoid Math.pow overflow.
    const exponent = Math.min(this.reconnectAttempts - 1, 10);
    const delay = Math.min(this.reconnectDelay * Math.pow(2, exponent), this.maxReconnectDelay);

    if (this.reconnectAttempts === 1 || this.reconnectAttempts % 10 === 0) {
      console.warn(`[WebSocket] Reconnect attempt #${this.reconnectAttempts} in ${delay}ms`);
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private handleMessage(message: WebSocketMessage): void {
    const { type, payload } = message;

    // ✅ FIX DEFECT-003: Use requestIdleCallback for non-blocking handler execution
    const processHandlers = () => {
      // Handle presence updates (can be deferred)
      if (type === 'presence_update') {
        this.presenceHandlers.forEach(handler => handler(payload));
        return;
      }

      // Handle collaboration events (can be deferred)
      if (type === 'collaboration_event') {
        this.collaborationHandlers.forEach(handler => handler(payload));
        return;
      }

      // Handle other message types with requestAnimationFrame to avoid blocking
      const handlers = this.messageHandlers.get(type);
      if (handlers) {
        handlers.forEach(handler => {
          requestAnimationFrame(() => handler(payload));
        });
      }
    };

    // Use requestIdleCallback with fallback for older browsers
    if ('requestIdleCallback' in window) {
      requestIdleCallback(processHandlers, { timeout: 100 });
    } else {
      setTimeout(processHandlers, 0);
    }
  }

  public sendMessage(message: WebSocketMessage): void {
    if (this.socket && this.socket.connected) {
      this.socket.emit('message', message);
    }
  }

  public subscribeToMessage(type: string, handler: (data: any) => void): () => void {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, []);
    }
    
    this.messageHandlers.get(type)!.push(handler);
    
    // Return unsubscribe function
    return () => {
      const handlers = this.messageHandlers.get(type);
      if (handlers) {
        const index = handlers.indexOf(handler);
        if (index > -1) {
          handlers.splice(index, 1);
        }
      }
    };
  }

  public subscribeToPresence(handler: (presence: UserPresence[]) => void): () => void {
    this.presenceHandlers.push(handler);
    
    return () => {
      const index = this.presenceHandlers.indexOf(handler);
      if (index > -1) {
        this.presenceHandlers.splice(index, 1);
      }
    };
  }

  public subscribeToCollaboration(handler: (event: CollaborationEvent) => void): () => void {
    this.collaborationHandlers.push(handler);
    
    return () => {
      const index = this.collaborationHandlers.indexOf(handler);
      if (index > -1) {
        this.collaborationHandlers.splice(index, 1);
      }
    };
  }

  public updateUserPresence(page: string, isActive: boolean = true): void {
    this.sendMessage({
      type: 'update_presence',
      payload: {
        currentPage: page,
        isActive,
        timestamp: Date.now()
      },
      timestamp: Date.now()
    });
  }

  public sendTypingIndicator(workflowId: string, isTyping: boolean): void {
    this.sendMessage({
      type: 'typing_indicator',
      payload: {
        workflowId,
        isTyping,
        timestamp: Date.now()
      },
      timestamp: Date.now()
    });
  }

  public broadcastWorkflowUpdate(workflowId: string, changes: any): void {
    this.sendMessage({
      type: 'workflow_update',
      payload: {
        workflowId,
        changes,
        timestamp: Date.now()
      },
      timestamp: Date.now()
    });
  }

  public disconnect(): void {
    this.desiredProjectIds.clear();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  public getConnectionState(): string {
    if (!this.socket) return 'disconnected';
    
    if (this.socket.connected) return 'connected';
    return 'disconnected';
  }

  /**
   * Re-identify the current socket with the latest credentials (call after login or
   * session refresh). Fixes the guest race (DEFECT-WS-GUEST-RACE): a socket that
   * connected before auth was available identified as a guest and was locked out of
   * every project room until a manual page reload. Re-identifying upgrades the socket to
   * the real user; the existing `user_identified` handler then rejoins desired projects —
   * no reload required. If the socket isn't up yet, connect() will identify on connect.
   */
  public reidentify(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnecting = false;
    this.connect();
  }
  
  /**
   * CRITICAL: Identify user to backend (REQUIRED for room-based events)
   * Backend expects this before any events can be received
   */
  private identifyUser(): void {
    if (!this.socket || !this.socket.connected) return;
    
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      const authState = (window as any).__authStore?.getState?.();
      if (authState?.user && authState?.isAuthenticated) {
        wsTrace('identify_emit', 'identify_user', { userId: authState.user.id, source: 'authStore' });
        this.socket.emit('identify_user', {
          userId: authState.user.id,
          name: authState.user.name,
          type: 'HUMAN' as const
        });
        return;
      }
      
      wsTrace('identify_emit', 'identify_user', { userId: 'guest', source: 'fallback-guest' });
      this.socket.emit('identify_user', {
        userId: `guest-${Date.now()}`,
        name: 'Guest User',
        type: 'HUMAN' as const
      });
      return;
    }
    
    try {
      const user = JSON.parse(userStr);
      wsTrace('identify_emit', 'identify_user', { userId: user.id, source: 'localStorage', guest: !user.id });
      this.socket.emit('identify_user', {
        userId: user.id || `guest-${Date.now()}`,
        name: user.name || user.email || 'Unknown User',
        type: (user.type || 'HUMAN') as 'HUMAN' | 'AGENT'
      });
    } catch (error) {
      console.error('[WebSocket] Failed to parse user from localStorage:', error);
    }
  }
  
  /**
   * CRITICAL: Join project room to receive project-specific events
   * Must be called when user navigates to a project
   */
  public joinProject(projectId: string): void {
    this.desiredProjectIds.add(projectId);
    wsTrace('join_emit', 'join_project', { project: projectId, connected: !!this.socket?.connected });
    if (this.socket?.connected) {
      this.socket.emit('join_project', projectId);
    }
  }
  
  public leaveProject(projectId: string): void {
    this.desiredProjectIds.delete(projectId);
    if (this.socket?.connected) {
      this.socket.emit('leave_project', projectId);
    }
  }

  private rejoinDesiredProjects(): void {
    if (!this.socket?.connected) return;
    this.desiredProjectIds.forEach((projectId) => {
      this.socket!.emit('join_project', projectId);
    });
  }
}

// Export singleton instance
export const webSocketService = new WebSocketService();
export default webSocketService;